import React, { useState, useEffect } from 'react';
import { Clock, DollarSign, FileText, Sparkles, ChevronRight, Plus, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AITimeSuggestion from '../AITimeSuggestion';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { formatCurrency } from '../../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TimeTrackingTab - World Class Minimalist Design
 */
const TimeTrackingTab = ({ caseId, timeEntries, onTimeAdded }) => {
  const { t, i18n } = useTranslation('caseWorkspace');
  const currentLanguage = i18n.language?.split('-')[0] || 'no';
  
  // State for tenant currency and user's hourly rate
  const [tenantCurrency, setTenantCurrency] = useState('NOK');
  const [userHourlyRate, setUserHourlyRate] = useState(1500);
  
  // Load tenant currency and user settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const token = auth.getToken();
        
        // Fetch tenant currency
        const tenantResponse = await axios.get(`${API}/admin/tenant`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (tenantResponse.data?.tenant?.currency) {
          setTenantCurrency(tenantResponse.data.tenant.currency);
        }
        
        // Fetch user's hourly rate
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (userResponse.data?.settings?.default_hourly_rate) {
          setUserHourlyRate(userResponse.data.settings.default_hourly_rate);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, []);
  
  // Use user's current hourly rate for all calculations
  const totalHours = timeEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
  const totalValue = totalHours * userHourlyRate;

  const fmtCurrency = (value) => formatCurrency(value, tenantCurrency, { showDecimals: false });

  const tr = (en: string, no: string) => currentLanguage === 'en' ? en : no;

  // Translation map for time entry descriptions
  const translateDescription = (desc: string): string => {
    if (currentLanguage !== 'en' || !desc) return desc;
    
    const translations: Record<string, string> = {
      // Common time entry descriptions
      'Utarbeiding av avtale-utkast': 'Draft agreement preparation',
      'Due diligence gjennomgang': 'Due diligence review',
      'Møte med klient': 'Client meeting',
      'oppstart Series B': 'Series B kickoff',
      'Gjennomgang av eksisterende aksjonæravtale': 'Review of existing shareholder agreement',
      'Gjennomgang av': 'Review of',
      'Redigering av': 'Editing of',
      'Utarbeiding av': 'Preparation of',
      'avtale-utkast': 'agreement draft',
      'aksjonæravtale': 'shareholder agreement',
      'Arbeid på sak': 'Case work',
      'Telefonsamtale med': 'Phone call with',
      'E-post korrespondanse': 'Email correspondence',
      'Dokumentgjennomgang': 'Document review',
      'Kontraktsanalyse': 'Contract analysis',
      'Forhandlinger': 'Negotiations',
      'eksisterende': 'existing',
      'klient': 'client',
      'motpart': 'counterparty',
    };
    
    let translated = desc;
    for (const [no, en] of Object.entries(translations)) {
      translated = translated.replace(new RegExp(no, 'gi'), en);
    }
    return translated;
  };

  return (
    <div className="min-h-full bg-white">
      <div className="max-w-5xl mx-auto px-8 py-10">
        
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">{tr('Hours', 'Timer')}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {tr('AI-assisted time tracking and registration', 'AI-assistert timeføring og registrering')}
              </p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex items-center gap-12 py-5 border-y border-gray-100">
            <div>
              <div className="text-2xl font-semibold text-gray-900">{totalHours.toFixed(1)}</div>
              <div className="text-xs text-gray-400 mt-0.5">{tr('Total hours', 'Timer totalt')}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-emerald-600">{fmtCurrency(totalValue)}</div>
              <div className="text-xs text-gray-400 mt-0.5">{tr('Value', 'Verdi')}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-gray-900">{timeEntries.length}</div>
              <div className="text-xs text-gray-400 mt-0.5">{tr('Entries', 'Registreringer')}</div>
            </div>
          </div>
        </header>

        {/* AI Time Suggestion */}
        <div className="mb-8">
          <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
            {tr('AI TIME TRACKING', 'AI Timeføring')}
          </h2>
          <AITimeSuggestion caseId={caseId} onTimeAdded={onTimeAdded} />
        </div>

        {/* Time Entries List */}
        <div>
          <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
            {tr('ALL ENTRIES', 'Alle registreringer')}
          </h2>
          
          {timeEntries.length > 0 ? (
            <div className="space-y-0">
              {timeEntries.map((entry, i) => (
                <div 
                  key={i} 
                  className="group flex items-center gap-4 py-3 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0 hover:bg-gray-50 -mx-2 px-2 rounded-lg"
                >
                  {/* Time indicator */}
                  <div className="w-0.5 h-8 rounded-full bg-violet-400 flex-shrink-0" />
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm text-gray-900 truncate">
                      {translateDescription(entry.description) || tr('Case work', 'Arbeid på sak')}
                    </h4>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {entry.date ? new Date(entry.date).toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'nb-NO', { 
                        day: 'numeric', 
                        month: 'short',
                        year: 'numeric'
                      }) : tr('Unknown date', 'Ukjent dato')}
                    </p>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {(entry.hours || 0).toFixed(1)} {currentLanguage === 'en' ? 'h' : 't'}
                    </p>
                    <p className="text-xs text-gray-400">
                      {fmtCurrency((entry.hours || 0) * userHourlyRate)}
                    </p>
                  </div>
                  
                  <ChevronRight className="w-4 h-4 text-gray-200 group-hover:text-gray-400 transition-colors" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-900 font-medium mb-1">{tr('No entries yet', 'Ingen registreringer ennå')}</p>
              <p className="text-sm text-gray-500 mb-4">
                {tr('AI will suggest hours based on your activity', 'AI vil foreslå timer basert på aktiviteten din')}
              </p>
            </div>
          )}
        </div>

        {/* AI Suggestion Box */}
        {timeEntries.length > 0 && (
          <div className="mt-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-5 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-violet-400" />
                <span className="text-xs font-medium text-gray-400">{tr('Lexy suggests', 'Lexy foreslår')}</span>
              </div>
              <p className="text-sm text-gray-300">
                {currentLanguage === 'en' 
                  ? `You have logged ${totalHours.toFixed(1)} hours with a total value of ${fmtCurrency(totalValue)}.${totalHours > 0 && totalHours < 8 ? ' Remember to log all hours for accurate billing.' : ''}`
                  : `Du har registrert ${totalHours.toFixed(1)} timer med en total verdi på ${fmtCurrency(totalValue)}.${totalHours > 0 && totalHours < 8 ? ' Husk å registrere alle timer for nøyaktig fakturering.' : ''}`
                }
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeTrackingTab;
